/** Human labels for truth profitability compute job phases (zeecore worker). */

const PHASE_LABELS: Record<string, string> = {
  discovering: 'Discovering fleet vehicles…',
  'vehicle-start': 'Daily vehicle summary…',
  'vehicle-progress': 'Daily vehicle summary…',
  persisting: 'Saving vehicle-day rows…',
  orders: 'Order profitability…',
  'telematics-stops': 'Delivery stops…',
  'order-costs': 'Order cost allocation…',
  'customer-profitability': 'Customer profitability…',
};

export function labelForProfitabilityComputePhase(phase: string | undefined): string | null {
  if (!phase) return null;
  return PHASE_LABELS[phase] ?? phase.replace(/-/g, ' ');
}

const VEHICLE_DAY_PHASES = new Set(['discovering', 'vehicle-start', 'vehicle-progress', 'persisting']);

type ComputeProgress = {
  phase?: string;
  /** Multi-day vehicle-day phase runs one day per worker invocation. */
  currentDay?: string;
  dayIndex?: number;
  totalDays?: number;
  currentVehicle?: string;
  processedVehicleDays?: number;
  totalVehicleDays?: number;
  orderCandidates?: number;
  orderBatchStart?: number;
  orderRecordsSoFar?: number;
  orderSubPhase?: string;
  stopsSubPhase?: string;
  stopsVehiclesPrefetched?: number;
  stopsVehicleDaysWithStops?: number;
};

export function profitabilityComputePhaseDetail(progress: ComputeProgress): string | null {
  const detail = phaseDetail(progress);
  if (
    detail
    && VEHICLE_DAY_PHASES.has(progress.phase ?? '')
    && progress.dayIndex != null
    && progress.totalDays != null
    && progress.totalDays > 1
  ) {
    return `Day ${progress.dayIndex}/${progress.totalDays}${progress.currentDay ? ` (${progress.currentDay})` : ''} · ${detail}`;
  }
  return detail;
}

function phaseDetail(progress: ComputeProgress): string | null {
  const label = labelForProfitabilityComputePhase(progress.phase);
  if (!label) return null;

  if (
    (progress.phase === 'vehicle-start' || progress.phase === 'vehicle-progress') &&
    progress.currentVehicle
  ) {
    const days =
      progress.processedVehicleDays != null && progress.totalVehicleDays != null
        ? ` · ${progress.processedVehicleDays}/${progress.totalVehicleDays} vehicle-days`
        : '';
    return `${label} (${progress.currentVehicle}${days})`;
  }

  if (progress.phase === 'orders' && progress.orderCandidates != null && progress.orderCandidates > 0) {
    const batchIdx =
      progress.orderBatchStart != null
        ? Math.floor(progress.orderBatchStart / 50) + 1
        : null;
    const batches = Math.ceil(progress.orderCandidates / 50);
    const sub =
      progress.orderSubPhase === 'prefetch'
        ? 'loading batch'
        : progress.orderSubPhase === 'building'
          ? 'building rows'
          : progress.orderSubPhase === 'persisting'
            ? 'saving'
            : null;
    const parts = [
      batchIdx != null ? `batch ${batchIdx}/${batches}` : null,
      sub,
      progress.orderRecordsSoFar != null ? `${progress.orderRecordsSoFar} rows` : null,
    ].filter(Boolean);
    return parts.length > 0 ? `${label} (${parts.join(' · ')})` : label;
  }

  if (progress.phase === 'telematics-stops' && progress.totalVehicleDays != null && progress.totalVehicleDays > 0) {
    const sub =
      progress.stopsSubPhase === 'prefetch-positions'
        ? 'loading GPS'
        : progress.stopsSubPhase === 'building'
          ? 'detecting stops'
          : progress.stopsSubPhase === 'persisting'
            ? 'saving'
            : null;
    const parts = [
      progress.stopsVehiclesPrefetched != null && progress.stopsSubPhase === 'prefetch-positions'
        ? `${progress.stopsVehiclesPrefetched} vehicles`
        : null,
      progress.processedVehicleDays != null
        ? `${progress.processedVehicleDays}/${progress.totalVehicleDays} vehicle-days`
        : null,
      progress.stopsVehicleDaysWithStops != null && progress.stopsSubPhase === 'building'
        ? `${progress.stopsVehicleDaysWithStops} with stops`
        : null,
      sub,
    ].filter(Boolean);
    return parts.length > 0 ? `${label} (${parts.join(' · ')})` : label;
  }

  return label;
}
